/**
 * The plan viewport: the page, the polygon being drawn, the calibration pair, and the
 * recognised geometry once it exists.
 *
 * Three layers, split by weight rather than by taste:
 *
 *   plan canvas      the rasterised page, re-rendered when the zoom changes
 *   result canvas    recognised geometry, which runs to thousands of segments - a wall count
 *                    of 1147 is a small case, and that many SVG nodes is a slideshow
 *   overlay svg      the polygon and the two calibration points; a handful of nodes, so SVG
 *                    buys crisp lines at any zoom and real focusable elements for free
 *
 * The SVG's viewBox is preview space, so a point is placed at its preview coordinate and the
 * browser does the transform. Nothing here converts screen pixels to preview pixels by hand,
 * which is the arithmetic most likely to be subtly wrong.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  previewToRotatedView,
  rotatedViewToPreview,
  type Point,
  type Size,
} from "@plan-forge/conversion-core";
import type { PlanPage } from "./plan-source.ts";
import type { Arc, RecognitionResult } from "./api.ts";
import { planToPreview, sheetPlanFor } from "./sheet-preview.ts";
import {
  CLOSE_RADIUS,
  MIN_POLYGON_POINTS,
  areaDraft,
  effectiveScale,
  floorIndexes,
  pageSetup,
  runIsCurrent,
  type CanvasMode,
  type ConversionState,
} from "./state.ts";

/** One per area, distinct from the wall, door and tag colours on the same canvas. */
const SHEET_COLOURS = ["#f87171", "#34d399", "#c084fc", "#facc15", "#22d3ee", "#fb7185", "#a3e635", "#f97316"];

/** Zoom bounds. Below the floor a sheet is unreadable; above the ceiling the canvas is huge. */
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 8;

/** One colour per floor, for all 12 a page can carry, so areas are told apart at a glance. */
const AREA_COLOURS = [
  "#22d3ee", "#f59e0b", "#a78bfa", "#34d399", "#f472b6", "#f87171",
  "#60a5fa", "#fbbf24", "#c084fc", "#2dd4bf", "#fb923c", "#a3e635",
] as const;

/** How long a zoom has to rest before the sheet and the geometry are redrawn sharp. */
const ZOOM_SETTLE_MS = 140;

/** Arrow-key pan step, in CSS pixels. Shift multiplies it. */
const PAN_STEP = 60;

export interface PlanCanvasProps {
  readonly page: PlanPage | null;
  readonly state: ConversionState;
  readonly mode: CanvasMode;
  /** By area key. Only those for the page on screen, and current, are drawn. */
  readonly results: Readonly<Record<string, RecognitionResult>>;
  readonly showResult: boolean;
  readonly onPlacePoint: (point: Point) => void;
  readonly onFinishArea: () => void;
  readonly onUndoPoint: () => void;
  readonly onExitMode: () => void;
  readonly onZoomChange: (zoom: number) => void;
  readonly zoom: number;
  readonly onFit: () => void;
}

export function PlanCanvas({
  page,
  state,
  mode,
  results,
  showResult,
  onPlacePoint,
  onFinishArea,
  onUndoPoint,
  onExitMode,
  onZoomChange,
  zoom,
  onFit,
}: PlanCanvasProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const planRef = useRef<HTMLCanvasElement>(null);
  const resultRef = useRef<HTMLCanvasElement>(null);
  const [cursor, setCursor] = useState<Point | null>(null);

  const preview: Size = page?.preview ?? { width: 1, height: 1 };
  const rotated = state.viewRotation === 90 || state.viewRotation === 270;
  const view: Size = rotated
    ? { width: preview.height, height: preview.width }
    : preview;

  /** Geometry for the floors on this page whose recognition matches what is drawn now. */
  const visible = useMemo(
    () =>
      floorIndexes(state, state.pageNumber).flatMap((areaIndex) => {
        const result = results[`${state.pageNumber}:${areaIndex}`];
        const current = runIsCurrent(state, { pageNumber: state.pageNumber, areaIndex, key: `${state.pageNumber}:${areaIndex}` });
        return result !== undefined && current ? [result] : [];
      }),
    [state, results],
  );

  // ---- the page itself -----------------------------------------------------------------

  /**
   * The displayed size, set synchronously and separately from rasterising.
   *
   * This used to happen after `page.render` resolved, which on a large sheet is hundreds of
   * milliseconds. In that window the element still had its previous CSS size while `zoom` had
   * already changed - and every click is converted to preview space by dividing out `zoom`
   * against this element's box. A calibration point placed during the gap was measured
   * against a stale size and silently landed in the wrong place, which is the worst kind of
   * wrong here: it produces a plausible scale rather than an error.
   *
   * A layout effect, so the geometry is correct before the browser paints and before any
   * click can be handled against it. The raster catching up later only makes it sharper.
   */
  useLayoutEffect(() => {
    for (const canvas of [planRef.current, resultRef.current]) {
      if (canvas === null) continue;
      canvas.style.width = `${view.width * zoom}px`;
      canvas.style.height = `${view.height * zoom}px`;
    }
  }, [zoom, view.width, view.height]);

  /**
   * Re-rasterised off screen and swapped in, a moment after the zoom settles.
   *
   * `page.render` resizes its canvas, which clears it, and a large sheet takes hundreds of
   * milliseconds - so rendering onto the visible canvas flashed the dark stage colour on every
   * zoom step. Until the sharper bitmap is ready the old one stays and is stretched by CSS
   * (see the layout effect above), which is what makes zooming feel continuous. The first
   * render of a page is not delayed: there is nothing to stretch yet.
   */
  const paintedPage = useRef<PlanPage | null>(null);
  useEffect(() => {
    const canvas = planRef.current;
    if (canvas === null || page === null) return;

    // Past 2x the extra pixels are past what a screen resolves, and a 6048x4320 sheet at 8x
    // would allocate gigabytes.
    const renderScale = Math.min(zoom * (window.devicePixelRatio || 1), 2);
    let cancelled = false;
    const delay = paintedPage.current === page ? ZOOM_SETTLE_MS : 0;
    const timer = setTimeout(() => {
      const buffer = document.createElement("canvas");
      void page.render(buffer, renderScale).then(() => {
        if (cancelled) return;
        canvas.width = buffer.width;
        canvas.height = buffer.height;
        canvas.getContext("2d")?.drawImage(buffer, 0, 0);
        paintedPage.current = page;
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [page, zoom, view.width, view.height]);

  // ---- recognised geometry -------------------------------------------------------------

  const sheetScale =
    state.outputType === "DwgFloorPlan" ? effectiveScale(state, state.pageNumber)?.scale ?? null : null;

  useEffect(() => {
    const canvas = resultRef.current;
    if (canvas === null || page === null) return;

    // The backing store is only resized when the drawing is redone, a moment after the zoom
    // settles; in between, CSS stretches the previous drawing instead of showing nothing.
    const timer = setTimeout(draw, canvas.width > 1 ? ZOOM_SETTLE_MS : 0);
    return () => clearTimeout(timer);

    function draw() {
      const canvas = resultRef.current;
      if (canvas === null) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(view.width * zoom * dpr);
      canvas.height = Math.round(view.height * zoom * dpr);
      canvas.style.width = `${view.width * zoom}px`;
      canvas.style.height = `${view.height * zoom}px`;

      const context = canvas.getContext("2d");
      if (context === null) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      if (visible.length === 0 || !showResult) return;

      const toView = (p: Point) => {
        const r = previewToRotatedView(p, preview, state.viewRotation);
        return { x: r.x * zoom * dpr, y: r.y * zoom * dpr };
      };

      const strokeSegments = (
        segments: readonly { start: Point; end: Point }[],
        colour: string,
        width: number,
      ) => {
        if (segments.length === 0) return;
        context.strokeStyle = colour;
        context.lineWidth = width * dpr;
        context.beginPath();
        for (const segment of segments) {
          const a = toView(segment.start);
          const b = toView(segment.end);
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
        }
        // One path for every segment of a kind, stroked once. Stroking per segment turns 1147
        // walls into 1147 draw calls and the pan starts dropping frames.
        context.stroke();
      };

      /** Polylines, door swings and splines: a list of points to join, under different names. */
      const strokeChains = (
        chains: readonly { readonly points: readonly Point[]; readonly closed?: boolean | undefined }[],
        colour: string,
        width: number,
      ) => {
        if (chains.length === 0) return;
        context.strokeStyle = colour;
        context.lineWidth = width * dpr;
        context.beginPath();
        for (const chain of chains) {
          chain.points.forEach((point, index) => {
            const p = toView(point);
            if (index === 0) context.moveTo(p.x, p.y);
            else context.lineTo(p.x, p.y);
          });
          if (chain.closed === true) context.closePath();
        }
        context.stroke();
      };

      /**
       * Arcs are arcs, not segments.
       *
       * The engine gives a centre, a radius and two angles, and sweepRadians carries the
       * direction as its sign. Drawing them as chords - which is what treating them as
       * segments does - turns every door swing and curved wall into a straight line.
       *
       * Radius scales with the zoom like every other length; the rotation is applied to the
       * centre, and the angles are offset by it so a rotated view does not unwind the sweep.
       */
      const strokeArcs = (arcs: readonly Arc[], colour: string, width: number) => {
        if (arcs.length === 0) return;
        const rotationRadians = (state.viewRotation * Math.PI) / 180;
        context.strokeStyle = colour;
        context.lineWidth = width * dpr;
        context.beginPath();
        for (const arc of arcs) {
          const centre = toView(arc.center);
          const radius = arc.radius * zoom * dpr;
          if (!(radius > 0)) continue;
          const start = arc.startAngleRadians + rotationRadians;
          const end = arc.endAngleRadians + rotationRadians;
          const anticlockwise = (arc.sweepRadians ?? end - start) < 0;
          context.moveTo(centre.x + radius * Math.cos(start), centre.y + radius * Math.sin(start));
          context.arc(centre.x, centre.y, radius, start, end, anticlockwise);
        }
        context.stroke();
      };

      for (const result of visible) {
        // Walls last would hide the doors under them at low zoom, so the lighter evidence goes on
        // top of the heavier structure.
        strokeSegments(result.wallLines, "#38bdf8", 1.4);
        strokeChains(
          result.polylines.map((line) => ({ points: line.vertices, closed: line.closed })),
          "#38bdf8",
          1.4,
        );
        strokeArcs(result.arcLines, "#818cf8", 1.2);
        strokeChains(
          result.splines.map((spline) => ({ points: spline.fitPoints })),
          "#818cf8",
          1.2,
        );
        strokeChains(
          result.doorGraphicLines.map((line) => ({ points: line.vertices, closed: line.closed })),
          "#f472b6",
          1.2,
        );

        // Each door is its swing: an arc from the closed leaf to the open one, and the open leaf
        // as a line, so the direction it faces reads at a glance. A double door has two. Results
        // from before swings were recorded carry only a position, shown as a short tick.
        context.strokeStyle = "#fb923c";
        context.lineWidth = 2 * dpr;
        context.beginPath();
        for (const door of result.doors) {
          if (door.swings === undefined || door.swings === null || door.swings.length === 0) {
            const c = toView(door.center);
            const angle = (((door.rotationDegrees ?? 0) + state.viewRotation) * Math.PI) / 180;
            const reach = 7 * dpr;
            context.moveTo(c.x - reach * Math.cos(angle), c.y - reach * Math.sin(angle));
            context.lineTo(c.x + reach * Math.cos(angle), c.y + reach * Math.sin(angle));
            continue;
          }
          for (const swing of door.swings) {
            const hinge = toView(swing.hinge);
            const closed = toView(swing.closedTip);
            const open = toView(swing.openTip);
            const radius = Math.hypot(closed.x - hinge.x, closed.y - hinge.y);
            if (!(radius > 0)) continue;
            const start = Math.atan2(closed.y - hinge.y, closed.x - hinge.x);
            const end = Math.atan2(open.y - hinge.y, open.x - hinge.x);
            const sweep = Math.atan2(Math.sin(end - start), Math.cos(end - start));
            context.moveTo(closed.x, closed.y);
            context.arc(hinge.x, hinge.y, radius, start, end, sweep < 0);
            context.moveTo(hinge.x, hinge.y);
            context.lineTo(open.x, open.y);
          }
        }
        context.stroke();

        for (const tag of result.roomTags) {
          const p = toView(tag.position);
          context.fillStyle = "#fbbf24";
          context.beginPath();
          context.arc(p.x, p.y, 3 * dpr, 0, Math.PI * 2);
          context.fill();
          const label = tag.roomNumber || tag.roomName1;
          if (label !== "" && zoom > 0.35) {
            context.font = `${11 * dpr}px ui-sans-serif, system-ui, sans-serif`;
            context.fillStyle = "#fde68a";
            context.fillText(label, p.x + 5 * dpr, p.y - 5 * dpr);
          }
        }
      }

      // The 03 sheets this page will be split into, over the geometry they are cut from.
      if (sheetScale === null) return;
      for (const result of visible) {
        const plan = sheetPlanFor(result, sheetScale);
        if (plan === null) continue;
        const toSheetView = (p: { x: number; y: number }) => toView(planToPreview(p, sheetScale));
        plan.sheets.forEach((sheet, index) => {
          const colour = SHEET_COLOURS[index % SHEET_COLOURS.length]!;
          context.fillStyle = `${colour}26`;
          context.beginPath();
          for (const polygon of sheet.hatchPolygons) {
            polygon.forEach((point, i) => {
              const v = toSheetView(point);
              if (i === 0) context.moveTo(v.x, v.y);
              else context.lineTo(v.x, v.y);
            });
            context.closePath();
          }
          context.fill();
          const centre = toSheetView(sheet.labelPoint);
          context.font = `600 ${28 * dpr}px ui-sans-serif, system-ui, sans-serif`;
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillStyle = colour;
          context.fillText(sheet.areaLetter, centre.x, centre.y);
          context.textAlign = "start";
          context.textBaseline = "alphabetic";
        });
        // Each sheet draws its own matchline on its own viewport edge, so a cut shows two lines;
        // each takes its sheet's colour so they read apart.
        context.lineWidth = 2.5 * dpr;
        context.setLineDash([10 * dpr, 6 * dpr]);
        for (const line of plan.matchlines) {
          context.strokeStyle = SHEET_COLOURS[(line.sheet - 1) % SHEET_COLOURS.length]!;
          context.beginPath();
          line.points.forEach((point, i) => {
            const v = toSheetView(point);
            if (i === 0) context.moveTo(v.x, v.y);
            else context.lineTo(v.x, v.y);
          });
          context.stroke();
        }
        context.setLineDash([]);
      }
    }
  }, [page, visible, showResult, sheetScale, zoom, state.viewRotation, preview, view.width, view.height]);

  // ---- pointer -------------------------------------------------------------------------

  /** Screen event to preview coordinate, via the rotation the operator is looking at. */
  const toPreview = useCallback(
    (event: { clientX: number; clientY: number }): Point | null => {
      const canvas = planRef.current;
      if (canvas === null) return null;
      const box = canvas.getBoundingClientRect();
      const inView = { x: (event.clientX - box.left) / zoom, y: (event.clientY - box.top) / zoom };
      return rotatedViewToPreview(inView, preview, state.viewRotation);
    },
    [zoom, preview, state.viewRotation],
  );

  const handleClick = (event: React.MouseEvent) => {
    if (mode === "pan") return;
    const point = toPreview(event);
    if (point !== null) onPlacePoint(point);
  };

  // Double-click finishes the area and right-click takes back the last vertex, as in the
  // desktop application. Only while drawing: elsewhere both keep the browser's own meaning.
  const handleDoubleClick = (event: React.MouseEvent) => {
    if (mode !== "draw") return;
    event.preventDefault();
    onFinishArea();
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    if (mode !== "draw") return;
    event.preventDefault();
    onUndoPoint();
  };

  /**
   * What the operator is pointing at, so a zoom can keep it under the cursor.
   *
   * Zooming about the centre of the viewport is close to useless on a sheet that is thirty
   * times the width of the window: the detail being examined leaves the screen on the way in.
   * The anchor is applied after the zoom has been laid out, in the layout effect below.
   */
  const anchor = useRef<{ preview: Point; clientX: number; clientY: number } | null>(null);

  const zoomAbout = useCallback(
    (next: number, event?: { clientX: number; clientY: number }) => {
      const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
      if (clamped === zoom) return;
      if (event !== undefined) {
        const point = toPreview(event);
        if (point !== null) {
          anchor.current = { preview: point, clientX: event.clientX, clientY: event.clientY };
        }
      }
      onZoomChange(clamped);
    },
    [zoom, onZoomChange, toPreview],
  );

  // Plain wheel zooms, which is what the desktop application does and what a plan viewer is
  // for. The browser convention of reserving that for scrolling belongs to pages that scroll;
  // here the canvas is a dedicated viewport and dragging is the way to pan.
  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    zoomAbout(zoom * (event.deltaY < 0 ? 1.12 : 1 / 1.12), event);
  };

  // Applied after the browser has laid the stage out at the new zoom, so the arithmetic is
  // against real geometry rather than a prediction of it.
  useLayoutEffect(() => {
    const held = anchor.current;
    const scroller = scrollRef.current;
    const canvas = planRef.current;
    anchor.current = null;
    if (held === null || scroller === null || canvas === null) return;

    const inView = previewToRotatedView(held.preview, preview, state.viewRotation);
    const box = canvas.getBoundingClientRect();
    // Where that preview point sits now, against where the pointer is.
    scroller.scrollLeft += box.left + inView.x * zoom - held.clientX;
    scroller.scrollTop += box.top + inView.y * zoom - held.clientY;
  }, [zoom, preview, state.viewRotation]);

  // WCAG 2.2 asks for a single-pointer alternative to dragging. Panning by keyboard is that
  // alternative, and it is also just faster for nudging a large sheet.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const scroller = scrollRef.current;
    if (scroller === null) return;
    const step = event.shiftKey ? PAN_STEP * 4 : PAN_STEP;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (mode === "draw" && event.key === "Enter") {
      event.preventDefault();
      onFinishArea();
      return;
    }
    if (mode !== "pan" && event.key === "Escape") {
      event.preventDefault();
      onExitMode();
      return;
    }

    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      scroller.scrollBy({ left: move[0], top: move[1], behavior: "auto" });
      return;
    }

    // The shortcuts every viewer has. Without them zoom is pointer-only, which is the same
    // gap WCAG's dragging rule closes for panning.
    if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoomAbout(zoom * 1.25);
    } else if (event.key === "-" || event.key === "_") {
      event.preventDefault();
      zoomAbout(zoom / 1.25);
    } else if (event.key === "0") {
      event.preventDefault();
      onFit();
    }
  };

  // Middle-drag and space-drag pan without leaving the current mode, so placing polygon points
  // across a big sheet does not mean switching tools for every scroll.
  const panning = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  const handlePointerDown = (event: React.PointerEvent) => {
    const scroller = scrollRef.current;
    if (scroller === null) return;
    const wantsPan = mode === "pan" || event.button === 1;
    if (!wantsPan) return;
    event.preventDefault();
    (event.target as Element).setPointerCapture?.(event.pointerId);
    panning.current = {
      x: event.clientX,
      y: event.clientY,
      left: scroller.scrollLeft,
      top: scroller.scrollTop,
    };
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const point = toPreview(event);
    setCursor(point);
    const scroller = scrollRef.current;
    const start = panning.current;
    if (scroller === null || start === null) return;
    scroller.scrollLeft = start.left - (event.clientX - start.x);
    scroller.scrollTop = start.top - (event.clientY - start.y);
  };

  const endPan = () => {
    panning.current = null;
  };

  // ---- overlay geometry, in preview coordinates ------------------------------------------

  const inView = useCallback(
    (p: Point) => previewToRotatedView(p, preview, state.viewRotation),
    [preview, state.viewRotation],
  );

  /** Every floor drawn on this page, each in its own colour, with the one being edited marked. */
  const floors = useMemo(
    () =>
      floorIndexes(state, state.pageNumber).flatMap((areaIndex) => {
        const draft = areaDraft(state, state.pageNumber, areaIndex);
        if (draft.polygon.length === 0) return [];
        const points = draft.polygon.map(inView);
        return [
          {
            areaIndex,
            closed: draft.polygonClosed,
            points,
            path: points.map((p) => `${p.x},${p.y}`).join(" "),
            colour: AREA_COLOURS[areaIndex % AREA_COLOURS.length] as string,
            active: areaIndex === state.areaIndex,
          },
        ];
      }),
    [state, inView],
  );

  /**
   * Where the next edge would go: from the last placed point to the pointer, and the closing
   * edge back to the first. Without it the drafter places points and hopes the line lands
   * where they meant.
   */
  const pointer = cursor === null ? null : inView(cursor);
  const drawing = mode === "draw" ? floors.find((f) => f.active && !f.closed) : undefined;
  const lastPoint = drawing?.points.at(-1);
  const firstPoint = drawing?.points[0];
  const wouldClose =
    drawing !== undefined &&
    cursor !== null &&
    drawing.points.length >= MIN_POLYGON_POINTS &&
    firstPoint !== undefined &&
    Math.hypot(pointer!.x - firstPoint.x, pointer!.y - firstPoint.y) <= CLOSE_RADIUS;

  const calibration = pageSetup(state, state.pageNumber).calibration;
  const calibrationFrom = calibration.from === null ? null : inView(calibration.from);
  const calibrationTo = calibration.to === null ? null : inView(calibration.to);

  // Scales overlay strokes so a line stays about the same thickness on screen at any zoom.
  const hair = 1 / zoom;

  if (page === null) {
    return (
      <div className="canvas-empty">
        <p className="canvas-empty__title">Open a PDF, JPG, or PNG to begin</p>
        <p className="canvas-empty__hint">The source preview will appear here.</p>
      </div>
    );
  }

  return (
    <div
      className={`canvas canvas--${mode}`}
      ref={scrollRef}
      tabIndex={0}
      role="application"
      aria-label={`Floor plan, page ${state.pageNumber}. Scroll wheel or plus and minus to zoom, arrow keys to pan, zero to fit.`}
      onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endPan}
      onPointerCancel={endPan}
      onPointerLeave={() => {
        endPan();
        setCursor(null);
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      <div
        className="canvas__stage"
        style={{ width: view.width * zoom, height: view.height * zoom }}
      >
        <canvas ref={planRef} className="canvas__layer" />
        <canvas ref={resultRef} className="canvas__layer canvas__layer--result" aria-hidden="true" />
        <svg
          className="canvas__layer canvas__layer--overlay"
          viewBox={`0 0 ${view.width} ${view.height}`}
          width={view.width * zoom}
          height={view.height * zoom}
          aria-hidden="true"
        >
          {floors.map((floor) => (
            <g key={floor.areaIndex} style={{ color: floor.colour }} opacity={floor.active ? 1 : 0.75}>
              {floor.closed ? (
                <polygon points={floor.path} className="ink-polygon" strokeWidth={(floor.active ? 2.5 : 2) * hair} />
              ) : (
                <polyline points={floor.path} className="ink-polyline" strokeWidth={2 * hair} />
              )}
              {floor.points.map((p, index) => (
                <circle
                  key={index}
                  cx={p.x}
                  cy={p.y}
                  r={4 * hair}
                  className={index === 0 ? "ink-vertex ink-vertex--first" : "ink-vertex"}
                  strokeWidth={1.5 * hair}
                />
              ))}
              {floor.points[0] !== undefined && (
                <text
                  x={floor.points[0].x + 8 * hair}
                  y={floor.points[0].y - 8 * hair}
                  className="ink-label"
                  fontSize={13 * hair}
                >
                  Area {floor.areaIndex + 1}
                </text>
              )}
            </g>
          ))}

          {drawing !== undefined && pointer !== null && lastPoint !== undefined && (
            <g style={{ color: drawing.colour }} pointerEvents="none">
              <line
                x1={lastPoint.x}
                y1={lastPoint.y}
                x2={wouldClose && firstPoint !== undefined ? firstPoint.x : pointer.x}
                y2={wouldClose && firstPoint !== undefined ? firstPoint.y : pointer.y}
                className="ink-rubber"
                strokeWidth={2 * hair}
              />
              {drawing.points.length >= 2 && firstPoint !== undefined && !wouldClose && (
                <line
                  x1={pointer.x}
                  y1={pointer.y}
                  x2={firstPoint.x}
                  y2={firstPoint.y}
                  className="ink-rubber ink-rubber--closing"
                  strokeWidth={1.5 * hair}
                />
              )}
              {wouldClose && firstPoint !== undefined && (
                <circle cx={firstPoint.x} cy={firstPoint.y} r={9 * hair} className="ink-close-target" strokeWidth={2 * hair} />
              )}
            </g>
          )}

          {mode === "calibrate" && calibrationFrom !== null && calibrationTo === null && pointer !== null && (
            <line
              x1={calibrationFrom.x}
              y1={calibrationFrom.y}
              x2={pointer.x}
              y2={pointer.y}
              className="ink-calibration ink-calibration--live"
              strokeWidth={2 * hair}
              pointerEvents="none"
            />
          )}

          {calibrationFrom !== null && calibrationTo !== null && (
            <line
              x1={calibrationFrom.x}
              y1={calibrationFrom.y}
              x2={calibrationTo.x}
              y2={calibrationTo.y}
              className="ink-calibration"
              strokeWidth={2 * hair}
            />
          )}
          {[calibrationFrom, calibrationTo].map(
            (p, index) =>
              p !== null && (
                <circle
                  key={index}
                  cx={p.x}
                  cy={p.y}
                  r={5 * hair}
                  className="ink-calibration-point"
                  strokeWidth={2 * hair}
                />
              ),
          )}
        </svg>
      </div>

      {/* On the canvas, not in a collapsed step in the sidebar. Zoom is the control somebody
          reaches for first on a large sheet, and it was previously reachable only through an
          accordion that starts closed - which is to say, not reachable. */}
      <div className="canvas__tools">
        <button type="button" className="tool" onClick={() => zoomAbout(zoom / 1.25)} aria-label="Zoom out" title="Zoom out (-)">
          −
        </button>
        <output className="tool__zoom" aria-live="polite" aria-label="Zoom level">
          {Math.round(zoom * 100)}%
        </output>
        <button type="button" className="tool" onClick={() => zoomAbout(zoom * 1.25)} aria-label="Zoom in" title="Zoom in (+)">
          +
        </button>
        <button type="button" className="tool tool--wide" onClick={onFit} title="Fit the whole page (0)">
          Fit
        </button>
      </div>

      {mode !== "pan" && (
        <div className="canvas__badge" aria-live="polite">
          {mode === "draw" &&
            `Area ${state.areaIndex + 1}: click to place points. Click the first point, double-click or press Enter to finish; right-click undoes a point; Esc stops.`}
          {mode === "calibrate" && "Click two points a known distance apart."}
        </div>
      )}

      {cursor !== null && mode !== "pan" && (
        <div className="canvas__readout">
          {Math.round(cursor.x)}, {Math.round(cursor.y)}
        </div>
      )}
    </div>
  );
}
